import { isScheduleBlocksEnabled } from "@/features/calendar/utils/utils";
import { CalendarSettingsModal } from "@/features/modals/CalendarSettingsModal";
import { CalendarFilterModal } from "@/features/modals/calendarFilterModal/CalendarFilterModal";
import { useModals } from "@/features/modals/hooks";
import { useCalendarTour } from "@/features/tour/calendarTour/CalendarTourProvider";
import styled from "@emotion/styled";
import {
  type CoworkerAndTeamSelectionMode,
  type TeamSelection,
  type UserInfo,
  isTeamSelection,
  useMyTeams,
  useTeam,
} from "@repo/api";
import {
  Badge,
  Button,
  Pill,
  Variables,
  chevronDownIcon,
  gearIcon,
  plusIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ButtonsContainer,
  CoworkerAndTeamSelectorPill,
  ResponsiveButton,
  SectionContainer,
  useSettings,
} from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useState } from "react";

import { WeekPicker } from "./WeekPicker";
import {
  type AppointmentFilterActions,
  type CalenderAppointmentsSearchFilters,
  isTeamSelected,
} from "./appointmentFilterReducer";
import { selectEmail, selectTeam } from "./appointmentFilterSelector";

const ResponsiveGridItem = styled.div<{
  start?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Variables.space50};

  @media (min-width: ${Variables.breakpointM}) {
    grid-template-columns: 1fr max-content;
  }
`;

const ResponsiveGridRow = styled(SectionContainer)<{
  rowS?: number;
  rowL?: number;
}>`
  display: grid;
  grid-row: ${({ rowS }) => rowS};
  justify-content: ${(props) =>
    props.justifyContent ? props.justifyContent : "start"};
  @media (min-width: ${Variables.breakpointL}) {
    grid-row: ${({ rowL }) => rowL};
  }
`;

const FilterBadge = styled(Badge)`
  margin-left: ${Variables.space25};
`;

interface Props {
  filters: CalenderAppointmentsSearchFilters;
  actions: AppointmentFilterActions;
  onUpdateTeams?: () => void;
  mutateSearchAppointments: () => void;
  availableScheduleShiftTypes?: string[];
}

export const CalendarFilter = ({
  filters,
  actions,
  onUpdateTeams,
  mutateSearchAppointments,
  availableScheduleShiftTypes = [],
}: Props) => {
  const { pushModal } = useModals();
  const { projectOverview } = useCalendarTour();
  const { data: myTeams } = useMyTeams();
  const { calendar } = useSettings();
  const { data: searchedTeam } = useTeam(
    filters.tableView === "TEAM"
      ? filters.teamCalendar.selection?.teamId
      : undefined
  );

  const [isCalendarFilterOpen, setIsCalendarFilterOpen] = useState(false);
  const [isCalendarSettingsOpen, setIsCalendarSettingsOpen] = useState(false);

  const scheduleBlocksEnabled = isScheduleBlocksEnabled(
    calendar,
    searchedTeam?.buCode
  );

  const filtersCount =
    filters.calendarFilters.filteredTasks.length +
    Number(Boolean(filters.calendarFilters.hideCanceledEvents)) +
    Number(Boolean(filters.calendarFilters.hideCompletedEvents)) +
    Number(
      scheduleBlocksEnabled &&
        Boolean(filters.calendarFilters.hideHiddenActivity)
    ) +
    Number(
      scheduleBlocksEnabled &&
        Boolean(filters.calendarFilters.hideScheduleBlocks)
    ) +
    (filters.calendarFilters.filteredScheduleTypes?.length ?? 0);
  const setSelected = (selected: TeamSelection | UserInfo[] | undefined) => {
    if (isTeamSelection(selected)) {
      if (filters.tableView === "TEAM") {
        const deselectedMemberIds = selected.members
          .filter((m) => !m.selected)
          .map((m) => m.emailId);
        actions.setTeamCalendar({
          selection: {
            kind: "team",
            teamId: selected.team.teamId,
            assignedToTeam: selected.assignedToTeam,
            deselectedMemberIds,
          },
          emailId: [],
        });
      }
    } else {
      const emailIds = selected?.map((el) => el.emailId) ?? [];
      if (filters.tableView === "TEAM") {
        if (!selected && isTeamSelected(filters.teamCalendar)) {
          actions.setTeamCalendar({
            selection: undefined,
            emailId: [],
          });
        } else if (isTeamSelected(filters.teamCalendar)) {
          const { selection } = filters.teamCalendar;
          const team = myTeams?.find((t) => t.teamId === selection.teamId);
          const deselectedMemberIds = team
            ? team.teamMembers
                .filter((m) => !selected?.some((c) => c.emailId === m.emailId))
                .map((m) => m.emailId)
            : [];
          actions.setTeamCalendar({
            selection: {
              ...selection,
              deselectedMemberIds,
            },
            emailId: [],
          });
        } else {
          actions.setTeamCalendar({
            emailId: emailIds,
          });
        }
      } else if (filters.tableView === "AGENDA") {
        actions.setAgendaCalendar({ emailId: emailIds });
      } else if (filters.tableView === "OVERVIEW") {
        actions.setOverviewCalendar({ emailId: emailIds });
      }
    }
  };

  const getCoWorkerSelectionMode = (): CoworkerAndTeamSelectionMode => {
    if (filters.tableView === "AGENDA") {
      return { coworkerSelectionMode: "SINGLE" };
    }

    if (filters.tableView === "OVERVIEW") {
      return { coworkerSelectionMode: "MULTI" };
    }

    if (filters.tableView === "TEAM") {
      return { selectTeamWithMembers: true, toggleTeamMembers: true };
    }

    return {
      coworkerSelectionMode: "MULTI",
      selectTeamWithMembers: true,
      selectOnlyTeam: true,
      toggleTeamMembers: true,
    };
  };

  const getInitialTaskData = () => {
    if (filters.tableView === "TEAM") {
      return {
        selectedTeam: selectTeam(filters),
      };
    } else {
      return { selectedCoworkers: selectEmail(filters) };
    }
  };

  const getTeamSelectionForModal = (): TeamSelection | string[] | undefined => {
    if (filters.tableView !== "TEAM") {
      return selectEmail(filters);
    }
    const sel = filters.teamCalendar.selection;
    if (!sel) {
      return selectEmail(filters);
    }
    if (sel.kind === "team") {
      const team =
        myTeams?.find((t) => t.teamId === sel.teamId) ?? searchedTeam;
      if (!team) {
        return undefined;
      }
      return {
        kind: "team",
        team,
        assignedToTeam: sel.assignedToTeam,
        members: team.teamMembers.map((m) => ({
          ...m,
          selected: !sel.deselectedMemberIds.includes(m.emailId),
        })),
      };
    }
    return selectEmail(filters);
  };

  return (
    <>
      <SectionContainer>
        <ResponsiveGridItem>
          <ResponsiveGridRow rowS={1} rowL={1}>
            <ResponsiveGridItem>
              <WeekPicker
                actions={actions}
                fromDate={filters.selectedDate}
                calendarMode={filters.tableView}
              />
            </ResponsiveGridItem>
          </ResponsiveGridRow>

          <ResponsiveGridRow rowS={2} rowL={1} justifyContent="end">
            <ResponsiveGridItem>
              <ButtonsContainer>
                <CoworkerAndTeamSelectorPill
                  key={projectOverview?.openTeamsModal.toString()}
                  pushModal={pushModal}
                  selectionMode={getCoWorkerSelectionMode()}
                  selected={getTeamSelectionForModal()}
                  setSelected={setSelected}
                  searchContext="CALENDAR"
                />

                <Pill
                  size="small"
                  label={
                    <>
                      {t("label.filter")}
                      {filtersCount !== 0 ? (
                        <FilterBadge
                          colour="black"
                          label={filtersCount}
                          size="small"
                        />
                      ) : null}
                    </>
                  }
                  trailingIcon={chevronDownIcon}
                  onClick={() => {
                    setIsCalendarFilterOpen(true);
                    trackAnalytics("calendar_filters");
                  }}
                />
                <ResponsiveButton
                  breakpoint="md"
                  text={t("label.new")}
                  icon={plusIcon}
                  showIcon
                  size="small"
                  type="primary"
                  onClick={() => {
                    pushModal({
                      type: "TASK",
                      initialData: getInitialTaskData(),
                      mutateSearchAppointments,
                      context: "CALENDAR",
                    });
                    trackAnalytics("add_new_event");
                  }}
                />

                <Button
                  size="small"
                  text={t("label.calendar_settings")}
                  iconOnly
                  ssrIcon={gearIcon}
                  onClick={() => {
                    setIsCalendarSettingsOpen(true);
                    trackAnalytics("calendar_settings");
                  }}
                />
              </ButtonsContainer>
            </ResponsiveGridItem>
          </ResponsiveGridRow>
        </ResponsiveGridItem>
      </SectionContainer>
      <CalendarFilterModal
        isOpen={isCalendarFilterOpen}
        closeModal={() => setIsCalendarFilterOpen(false)}
        filters={filters.calendarFilters}
        onFilteredTaskChange={actions.setFilteredTasks}
        onHideCanceledEventsChange={actions.setHideCanceledEvents}
        onHideCompletedEventsChange={actions.setHideCompletedEvents}
        onHideScheduleBlocksChange={actions.setHideScheduleBlocks}
        onHideHiddenActivityChange={actions.setHideHiddenActivity}
        onFilteredScheduleTypesChange={actions.setFilteredScheduleTypes}
        availableScheduleShiftTypes={availableScheduleShiftTypes}
        selectedTeam={selectTeam(filters)}
      />
      {isCalendarSettingsOpen && (
        <CalendarSettingsModal
          isOpen={true}
          closeModal={() => setIsCalendarSettingsOpen(false)}
          settings={filters.calendarSettings}
          selectedTeam={selectTeam(filters)}
          onChangeSettings={actions.setCalendarSettings}
          onUpdateTeams={onUpdateTeams}
          tableView={filters.tableView}
        />
      )}
    </>
  );
};
